import { useGetIdentityOrganizations } from '@/hooks/queries/identity';
import { useCanAccessAnalyticsDashboard } from '@/hooks/queries/permissions';
import { useEscapeClosableDropdown } from '@/hooks/useEscapeClosableDropdown';
import { useMenuScrollShadow } from '@/hooks/useMenuScrollShadow';
import { useSsoLogin } from '@/hooks/useSsoLogin';
import { useAuthStore } from '@/store/use-auth-store';
import { getSidebarActiveGradient } from '@/utils/colors';
import { getTenantDisplayName, resolveTenantDisplayName } from '@/utils/tenant-display';
import { reloadIntoTenant } from '@/utils/switch-tenant';
import { useAuth0 } from '@auth0/auth0-react';
import { Icon } from '@iconify/react';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  ScrollShadow,
  Skeleton,
  Tooltip,
  cn,
} from '@nextui-org/react';
import { LayoutGroup } from 'framer-motion';
import { memo, useCallback, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import logo from '../../assets/logo.png';
import {
  getLiftHubPath,
  isLiftHubActive,
  isSoulMapConsumerSession,
} from '@/utils/soulmap-auth';
import { useSidebarStore } from '../../store/use-sidebar-store';
import { RecentChats } from './recent-chats';

interface IdentityOrganization {
  id: string;
  name: string;
  display_name: string;
}

const getNavItems = (tenantSlug: string, canAccessDashboard: boolean) => {
  const soulMapConsumer = isSoulMapConsumerSession();

  return [
    {
      title: 'LiFT Hub',
      path: getLiftHubPath(tenantSlug),
      icon: 'lucide:home',
      mobileOnly: false,
    },
    {
      title: 'Discover',
      path: `/${tenantSlug}/discover`,
      icon: 'lucide:compass',
      mobileOnly: true, // Hide on desktop, show only on mobile
    },
    {
      title: 'Library',
      path: `/${tenantSlug}/library`,
      icon: 'lucide:book-open',
      mobileOnly: false,
      hidden: soulMapConsumer,
    },
    {
      title: 'Dashboard',
      path: `/${tenantSlug}/dashboard`,
      icon: 'lucide:bar-chart-3',
      mobileOnly: false,
      hidden: soulMapConsumer || !canAccessDashboard,
    },
  ];
};

interface NavItemProps {
  path: string;
  icon: string;
  title: string;
  isExpanded: boolean;
  isActive: boolean;
}

const NavItem = memo(
  ({ path, icon, title, isExpanded, isActive }: NavItemProps) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <Link
        to={path}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={cn(
          'group relative flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-normal transition-all duration-200 ease-in-out',
          isActive
            ? 'text-white shadow-md'
            : 'text-foreground-600 hover:bg-default-100',
          // Hover transform: translateX when expanded, scale when collapsed
          !isActive && (isExpanded ? 'hover:translate-x-1' : 'hover:scale-105')
        )}
        style={
          isActive
            ? { background: getSidebarActiveGradient(isHovered ? 0.85 : 0.75) }
            : undefined
        }
      >
        <Icon
          icon={icon}
          className={cn(
            'h-5 w-5 flex-shrink-0 transition-colors',
            isActive ? 'text-white' : 'text-default-500'
          )}
        />
        {isExpanded && (
          <span
            className={cn(
              'truncate !font-weight-normal',
              isActive && 'font-medium'
            )}
          >
            {title}
          </span>
        )}
      </Link>
    );
  }
);

NavItem.displayName = 'NavItem';

export function DashboardSidebar() {
  const { isExpanded, toggleExpanded } = useSidebarStore();
  const location = useLocation();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const {
    authMethod,
    tenants,
    setShowTenantSelector,
  } = useAuthStore();
  const { getIdTokenClaims } = useAuth0();
  const { data: organizations } = useGetIdentityOrganizations(
    authMethod === 'sso'
  );
  const { loginWithSso } = useSsoLogin();
  const tenantDropdown = useEscapeClosableDropdown();
  const tenantMenuScroll = useMenuScrollShadow();
  const { hasPermission: canAccessDashboard, isPending: isNavPending } =
    useCanAccessAnalyticsDashboard();

  // While permissions load, skeletons stand in for every item that may show.
  const navItems = getNavItems(
    tenantSlug || '',
    isNavPending || canAccessDashboard
  ).filter((item) => !item.mobileOnly && !item.hidden);

  const isActiveRoute = (path: string) =>
    isLiftHubActive(location.pathname, path);

  const handleToggle = useCallback(() => {
    toggleExpanded();
  }, [toggleExpanded]);

  const currentDisplay = resolveTenantDisplayName({
    tenantSlug,
    authMethod,
    tenants,
    organizations: organizations as IdentityOrganization[] | undefined,
  });

  const handleTenantSwitch = async (key: string) => {
    if (key === tenantSlug) return;

    if (authMethod === 'sso') {
      // SSO Flow
      const newOrg = (organizations as IdentityOrganization[])?.find(
        (o: IdentityOrganization) => o.name === key
      );
      if (!newOrg) return;

      const idToken = (await getIdTokenClaims())?.__raw;
      if (!idToken) return;

      await loginWithSso({
        authorizationParams: {
          organization: newOrg.id,
          prompt: 'none',
          id_token_hint: idToken,
        },
      });
      window.location.reload();
    } else {
      // Username/Password Flow
      const newTenant = tenants?.find((t) => t.slug === key);
      if (!newTenant) return;

      setShowTenantSelector(false);
      reloadIntoTenant(newTenant.slug);
    }
  };

  return (
    <aside
      className={cn(
        'group relative flex h-full flex-col bg-white transition-all duration-300',
        isExpanded ? 'w-64' : 'w-16 cursor-pointer'
      )}
    >
      {/* Right edge vertical divider with shadow */}
      <div className="absolute right-0 top-0 h-full w-px bg-gray-200 shadow-[2px_0_8px_rgba(0,0,0,0.06)]" />
      {/* Header */}
      <div className="flex h-16 items-center justify-center px-2 flex-shrink-0 border-b border-gray-100 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
        {isExpanded ? (
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-3 overflow-hidden">
              <img
                src={logo}
                alt="LiFT Logo"
                className="h-6 w-6 flex-shrink-0"
              />
              {((authMethod === 'sso' &&
                organizations &&
                organizations.length > 1) ||
                (authMethod !== 'sso' && tenants && tenants.length > 1)) && (
                <Dropdown {...tenantDropdown.dropdownProps}>
                  <Tooltip content={currentDisplay} placement="bottom">
                    <div className="max-w-full">
                      <DropdownTrigger>
                        <Button
                          {...tenantDropdown.triggerProps}
                          variant="light"
                          className="min-w-0 px-2 text-sm font-medium capitalize"
                          endContent={
                            <Icon
                              icon="lucide:chevron-down"
                              className="text-default-500 flex-shrink-0"
                              width={16}
                            />
                          }
                        >
                          <span className="truncate max-w-[120px]">
                            {currentDisplay}
                          </span>
                        </Button>
                      </DropdownTrigger>
                    </div>
                  </Tooltip>
                  <DropdownMenu
                    aria-label="Tenants"
                    ref={tenantMenuScroll.ref}
                    classNames={{ base: tenantMenuScroll.className }}
                    onAction={(key) => handleTenantSwitch(key as string)}
                  >
                    {authMethod === 'sso'
                      ? (organizations || []).map((org: any) => (
                          <DropdownItem key={org.name}>
                            {getTenantDisplayName(org.display_name)}
                          </DropdownItem>
                        ))
                      : (tenants || []).map((tenant) => (
                          <DropdownItem key={tenant.slug}>
                            {getTenantDisplayName(tenant.name)}
                          </DropdownItem>
                        ))}
                  </DropdownMenu>
                </Dropdown>
              )}
            </div>
            <button
              onClick={handleToggle}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-default-600 transition-colors hover:bg-default-100"
            >
              <Icon icon="lucide:chevron-left" width={20} />
            </button>
          </div>
        ) : (
          <div className="relative h-6 w-6 flex items-center justify-center">
            <img
              src={logo}
              alt="LiFT Logo"
              className="h-6 w-6 transition-opacity duration-200 group-hover:opacity-0"
            />
            <Icon
              icon="lucide:chevron-right"
              width={20}
              className="absolute text-default-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              onClick={handleToggle}
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <ScrollShadow className="flex-1 px-2 py-4">
          <LayoutGroup>
            {/* Main Navigation - filter out mobile-only items on desktop */}
            <div className="space-y-1 pb-4 mb-4 border-b border-gray-100 ">
              {navItems.map((item) =>
                isNavPending ? (
                  <Skeleton
                    key={item.path}
                    className={cn(
                      'h-10 rounded-xl',
                      isExpanded ? 'w-full' : 'w-10'
                    )}
                  />
                ) : (
                  <NavItem
                    key={item.path}
                    path={item.path}
                    icon={item.icon}
                    title={item.title}
                    isExpanded={isExpanded}
                    isActive={isActiveRoute(item.path)}
                  />
                )
              )}
            </div>

            {/* Recent Chats Section */}
            <RecentChats isExpanded={isExpanded} />
          </LayoutGroup>
        </ScrollShadow>
      </div>
    </aside>
  );
}
